import { Box } from "@mui/material";

import { TAB_BAR_HEIGHT } from "../constants";
import type { DropTarget } from "../hooks/useTabDrag";
import type { SplitLayout } from "../hooks/useTabGroup";
import type { ImpersonationSession } from "../types";
import { SessionTab } from "./SessionTab";

interface SplitTabBarProps {
  dockedSessions: ImpersonationSession[];
  splitLayout: SplitLayout;
  dropZoneRect: { x: number; y: number; width: number; height: number } | null;
  onSelect: (id: string) => void;
  onRefresh: (id: string) => void;
  onPopOut: (id: string) => void;
  onClose: (id: string) => void;
  onDragToSplit: (id: string, side: "left" | "right") => void;
  onDragOut: (id: string) => void;
  onExitSplit: () => void;
  onDragStateChange: (isDragging: boolean, dropTarget: DropTarget) => void;
}

export function SplitTabBar({
  dockedSessions,
  splitLayout,
  dropZoneRect,
  onSelect,
  onRefresh,
  onPopOut,
  onClose,
  onDragToSplit,
  onDragOut,
  onExitSplit,
  onDragStateChange,
}: SplitTabBarProps) {
  return (
    <>
      {/* Left half of tab bar */}
      <Box sx={{ display: "flex", flex: 1, gap: 0.25, justifyContent: "flex-start" }}>
        {dockedSessions
          .filter((s) => s.id === splitLayout.leftSessionId)
          .map((session) => (
            <SessionTab
              key={session.id}
              isActive
              session={session}
              dropZoneRect={dropZoneRect}
              splitSide="left"
              onSelect={() => {
                onSelect(session.id);
              }}
              onRefresh={() => {
                onRefresh(session.id);
              }}
              onPopOut={() => {
                onPopOut(session.id);
              }}
              onClose={() => {
                onClose(session.id);
              }}
              onDragToSplit={(side) => {
                onDragToSplit(session.id, side);
              }}
              onDragOut={() => {
                onDragOut(session.id);
              }}
              onExitSplit={onExitSplit}
              onDragStateChange={onDragStateChange}
            />
          ))}
      </Box>
      {/* Divider */}
      <Box
        sx={{
          width: 2,
          height: TAB_BAR_HEIGHT - 4,
          backgroundColor: "common.white",
          opacity: 0.6,
          flexShrink: 0,
          borderRadius: 1,
          alignSelf: "center",
        }}
      />
      {/* Right half of tab bar */}
      <Box sx={{ display: "flex", flex: 1, gap: 0.25, justifyContent: "flex-start" }}>
        {dockedSessions
          .filter((s) => s.id === splitLayout.rightSessionId)
          .map((session) => (
            <SessionTab
              key={session.id}
              isActive
              session={session}
              dropZoneRect={dropZoneRect}
              splitSide="right"
              onSelect={() => {
                onSelect(session.id);
              }}
              onRefresh={() => {
                onRefresh(session.id);
              }}
              onPopOut={() => {
                onPopOut(session.id);
              }}
              onClose={() => {
                onClose(session.id);
              }}
              onDragToSplit={(side) => {
                onDragToSplit(session.id, side);
              }}
              onExitSplit={onExitSplit}
              onDragOut={() => {
                onDragOut(session.id);
              }}
              onDragStateChange={onDragStateChange}
            />
          ))}
        {/* Other docked tabs (not in split) */}
        {dockedSessions
          .filter((s) => s.id !== splitLayout.leftSessionId && s.id !== splitLayout.rightSessionId)
          .map((session) => (
            <SessionTab
              key={session.id}
              session={session}
              isActive={false}
              dropZoneRect={dropZoneRect}
              onSelect={() => {
                onSelect(session.id);
              }}
              onRefresh={() => {
                onRefresh(session.id);
              }}
              onPopOut={() => {
                onPopOut(session.id);
              }}
              onClose={() => {
                onClose(session.id);
              }}
              onDragToSplit={(side) => {
                onDragToSplit(session.id, side);
              }}
              onDragOut={() => {
                onDragOut(session.id);
              }}
              onDragStateChange={onDragStateChange}
            />
          ))}
      </Box>
    </>
  );
}
